import { useState } from "react";
import { Box, ButtonBase, Divider, Link as MuiLink, Popover, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { StatusChip } from "@/components/common/fields/StatusChipField";
import { StoplossClaim } from "@/types";
import { ColorType, UIComponentSize } from "@/types/shared/uiComponents";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { formatShortDate } from "@/utils/formatters/dateFormatter";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { getStatusColor } from "@/utils/helpers/getStatusColor";
import { auraTrackChip, carrierTrackChip } from "../../twoTrackState";
import { useStoplossClaimContext } from "../../hooks/useStoplossClaimContext";
import { StoplossClaimPreviewDialog } from "../StoplossClaimPreviewDialog";

/**
 * Peek at the claimant's other claims on this policy (#7582): the netting set.
 * Anchored to the "already on N other claims" clause. A row opens the existing
 * claim preview dialog (the one the document review grid uses), so the analyst
 * can read a sibling without leaving this claim.
 */
export const OtherClaimsPeek = ({ anchorEl, onClose }: { anchorEl: HTMLElement | null; onClose: () => void }) => {
  const { displayClaimantId, claimant, otherClaims, priorLive, priorStored, isFrozen, isFamily } =
    useStoplossClaimContext();
  const [previewId, setPreviewId] = useState<string | null>(null);

  return (
    <>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { width: 420, p: 2, borderRadius: 3 } } }}
      >
        <Box data-testid="other-claims-peek">
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600 }}>Other claims on this policy</Typography>
          <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mb: 1 }}>
            {isFamily
              ? "Family deductible policy: the engine nets against every family member's claims on the policy, not only this claimant's. The claimant's own claims:"
              : isFrozen
                ? `This claim was netted against ${formatUSD(priorStored)} when it was filed. The claimant's claims today:`
                : "The engine nets this claim against every other claim for this claimant on the policy."}
          </Typography>
          {otherClaims.length === 0 ? (
            <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>No other claims.</Typography>
          ) : (
            <Stack divider={<Divider />}>
              {otherClaims.map((c: StoplossClaim) => {
                const aura = auraTrackChip(c);
                const carrier = carrierTrackChip(c);
                return (
                  <ButtonBase
                    key={String(c.id)}
                    onClick={() => setPreviewId(String(c.id))}
                    sx={{
                      display: "block",
                      textAlign: "left",
                      width: "100%",
                      py: 1,
                      borderRadius: 1,
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "center" }}>
                      <Box sx={{ display: "flex", gap: 0.75, alignItems: "center", minWidth: 0, flexWrap: "wrap" }}>
                        <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600 }}>
                          {formatToTitleCase(c.stoploss_claim_type || "Claim")}
                        </Typography>
                        <StatusChip
                          label={formatToTitleCase(c.claim_status || "—")}
                          color={getStatusColor(c.claim_status) ?? ColorType.Default}
                          size={UIComponentSize.Small}
                        />
                        {aura ? (
                          <StatusChip label={aura.label} color={aura.color} size={UIComponentSize.Small} />
                        ) : null}
                        {carrier ? (
                          <StatusChip label={carrier.label} color={carrier.color} size={UIComponentSize.Small} />
                        ) : null}
                      </Box>
                      <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, whiteSpace: "nowrap" }}>
                        {formatUSD(c.total_paid_amount ?? 0)}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mt: 0.25 }}>
                      Created {c.created_datetime ? formatShortDate(c.created_datetime) : "—"}
                      {c.first_service_date ? `, service from ${formatShortDate(c.first_service_date)}` : ""}
                    </Typography>
                  </ButtonBase>
                );
              })}
              {isFamily ? null : (
                <Box sx={{ display: "flex", justifyContent: "space-between", pt: 1 }}>
                  <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600 }}>On other claims today</Typography>
                  <Typography sx={{ fontSize: "0.8125rem", fontWeight: 700 }}>{formatUSD(priorLive ?? 0)}</Typography>
                </Box>
              )}
            </Stack>
          )}
          {displayClaimantId ? (
            <Typography sx={{ fontSize: "0.8125rem", mt: 1.5 }}>
              <MuiLink component={RouterLink} to={`/claimants/${displayClaimantId}/show/claims`} underline="hover">
                All claims for this claimant
              </MuiLink>
            </Typography>
          ) : null}
        </Box>
      </Popover>
      <StoplossClaimPreviewDialog
        open={!!previewId}
        onClose={() => setPreviewId(null)}
        stoplossClaimId={previewId}
        claimantName={claimant?.claimant_name}
      />
    </>
  );
};
